import classNames from "classnames";
import type { FC, ReactNode } from "react";
import styles from "./index.module.css";

/**
 * The glyph from the app icon (public/logo.svg): stacked lyric lines and a
 * play mark, without the tile, so it can take any text color.
 */
export const BrandMark: FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		viewBox="34 34 152 152"
		fill="currentColor"
		aria-hidden="true"
		focusable="false"
	>
		<path d="M54.9713 58C50.6742 58 48.5256 58 46.9608 58.9929C46.5642 59.2444 46.198 59.5383 45.8684 59.8684C44.7139 61.0224 44 62.6171 44 64.3786C44 66.1402 44.7139 67.7349 45.8684 68.8889C46.198 69.219 46.5642 69.5129 46.9608 69.7643C48.5256 70.7573 50.6742 70.7573 54.9713 70.7573H132.961C137.258 70.7573 139.406 70.7573 140.971 69.7643C141.368 69.5129 141.734 69.219 142.064 68.8889C143.218 67.7349 143.932 66.1402 143.932 64.3786C143.932 62.6171 143.218 61.0224 142.064 59.8684C141.734 59.5383 141.368 59.2444 140.971 58.9929C139.406 58 137.258 58 132.961 58H54.9713Z" />
		<path d="M86.8643 87.767C82.5672 87.767 80.4187 87.767 78.8538 88.7599C78.4573 89.0113 78.091 89.3053 77.7615 89.6354C76.6069 90.7894 75.8931 92.3841 75.8931 94.1456C75.8931 95.9072 76.6069 97.5018 77.7615 98.6558C78.091 98.9859 78.4573 99.2799 78.8538 99.5313C80.4187 100.524 82.5672 100.524 86.8643 100.524H164.854C169.151 100.524 171.299 100.524 172.864 99.5313C173.261 99.2799 173.627 98.9859 173.957 98.6558C175.111 97.5018 175.825 95.9072 175.825 94.1456C175.825 92.3841 175.111 90.7894 173.957 89.6354C173.627 89.3053 173.261 89.0113 172.864 88.7599C171.299 87.767 169.151 87.767 164.854 87.767H86.8643Z" />
		<path d="M54.9713 117.534C50.6742 117.534 48.5256 117.534 46.9608 118.527C46.5642 118.778 46.198 119.072 45.8684 119.402C44.7139 120.556 44 122.151 44 123.913C44 125.674 44.7139 127.269 45.8684 128.423C46.198 128.753 46.5642 129.047 46.9608 129.298C48.5256 130.291 50.6742 130.291 54.9713 130.291H132.961C137.258 130.291 139.406 130.291 140.971 129.298C141.368 129.047 141.734 128.753 142.064 128.423C143.218 127.269 143.932 125.674 143.932 123.913C143.932 122.151 143.218 120.556 142.064 119.402C141.734 119.072 141.368 118.778 140.971 118.527C139.406 117.534 137.258 117.534 132.961 117.534H54.9713Z" />
		<path d="M86.8643 147.301C82.5672 147.301 80.4187 147.301 78.8538 148.294C78.4573 148.545 78.091 148.839 77.7615 149.169C76.6069 150.323 75.8931 151.918 75.8931 153.68C75.8931 155.441 76.6069 157.036 77.7615 158.19C78.091 158.52 78.4573 158.814 78.8538 159.065C80.4187 160.058 82.5672 160.058 86.8643 160.058H164.854C169.151 160.058 171.299 160.058 172.864 159.065C173.261 158.814 173.627 158.52 173.957 158.19C175.111 157.036 175.825 155.441 175.825 153.68C175.825 151.918 175.111 150.323 173.957 149.169C173.627 148.839 173.261 148.545 172.864 148.294C171.299 147.301 169.151 147.301 164.854 147.301H86.8643Z" />
		<path d="M44 88.449C44 84.718 44 82.8528 44.9159 81.9215C45.3698 81.4601 45.9535 81.1475 46.5897 81.0258C47.8718 80.7802 49.424 81.8152 52.5288 83.8845L61.0735 89.5812C63.6377 91.2906 64.9193 92.1454 65.2398 93.2792C65.3998 93.8458 65.3998 94.4454 65.2398 95.012C64.9193 96.1458 63.6377 97.0006 61.0735 98.7101L52.5288 104.407C49.424 106.476 47.8718 107.511 46.5897 107.265C45.9535 107.144 45.3698 106.831 44.9159 106.369C44 105.438 44 103.573 44 99.8423V88.449Z" />
	</svg>
);

/**
 * The lavender brand band: a deep field with the logo glyph cropped behind a
 * corner and the inks remapped so Radix text and buttons inside stay legible.
 * It only draws under the Spicy Lyrics design; otherwise children render as
 * they were.
 *
 * Use it on rare surfaces (first run). On the editor it would compete with the
 * lyrics.
 */
export const brandBandClassName = classNames("sl-band", styles.band);

/** The cropped glyph for a band. Render it as the band's first child. */
export const BrandBandMark: FC = () => (
	<span className={styles.mark} aria-hidden="true">
		<BrandMark />
	</span>
);

export const BrandBand: FC<{ className?: string; children: ReactNode }> = ({
	className,
	children,
}) => (
	<div className={classNames(brandBandClassName, className)}>
		<BrandBandMark />
		{children}
	</div>
);
